/**
 * Passenger management composable
 * Provides utilities for passenger data handling
 */
export function usePassengers() {
   /**
    * Calculates total passenger count from paxCounts object
    * @param {Object} paxCounts - Object with ADULT, CHILD, INFANT counts
    * @returns {number} Total passenger count
    */
   const calculateTotalPax = (paxCounts) => {
      if (!paxCounts) return 0;
      return (paxCounts.ADULT || 0) + (paxCounts.CHILD || 0) + (paxCounts.INFANT || 0);
   };

   /**
    * Sanitizes name input to allow only alphabetic characters
    * Uses the alphabetic() string extension method
    * @param {Object} passenger - Passenger object with name property
    */
   const sanitizeName = (passenger) => {
      if (passenger.name && typeof passenger.name.alphabetic === "function") {
         passenger.name = passenger.name.alphabetic();
      }
   };

   /**
    * Sanitizes surname input to allow only alphabetic characters
    * Uses the alphabetic() string extension method
    * @param {Object} passenger - Passenger object with surname property
    */
   const sanitizeSurname = (passenger) => {
      if (passenger.surname && typeof passenger.surname.alphabetic === "function") {
         passenger.surname = passenger.surname.alphabetic();
      }
   };

   /**
    * Initializes empty passenger list based on paxCounts
    * @param {Object} paxCounts - Object with ADULT, CHILD, INFANT counts
    * @returns {Array} Array of passenger objects with default values
    */
   const initializePassengers = (paxCounts) => {
      if (!paxCounts) return [];

      const passengers = [];

      // Add adults (type: 1)
      for (let i = 0; i < (paxCounts.ADULT || 0); i++) {
         passengers.push({
            type: 1,
            name: "",
            surname: "",
            gender: 1,
            birthdate: "",
         });
      }

      // Add children (type: 2)
      for (let i = 0; i < (paxCounts.CHILD || 0); i++) {
         passengers.push({
            type: 2,
            name: "",
            surname: "",
            gender: 1,
            birthdate: "",
         });
      }

      // Add infants (type: 3)
      for (let i = 0; i < (paxCounts.INFANT || 0); i++) {
         passengers.push({
            type: 3,
            name: "",
            surname: "",
            gender: 1,
            birthdate: "",
         });
      }

      return passengers;
   };

   /**
    * Validates passenger data
    * @param {Object} passenger - Passenger object to validate
    * @returns {boolean} True if valid, false otherwise
    */
   const validatePassenger = (passenger) => {
      const name = passenger.name?.trim() || "";
      const surname = passenger.surname?.trim() || "";
      const birthdate = passenger.birthdate?.trim() || "";

      return name.length >= 2 && name.length <= 30 && surname.length >= 2 && surname.length <= 30 && !!birthdate;
   };

   /**
    * Validates all passengers in a list
    * @param {Array} passengers - Array of passenger objects
    * @returns {Object} Validation result with isValid flag and invalidPassengers array
    */
   const validateAllPassengers = (passengers) => {
      const invalidPassengers = passengers.filter((passenger) => !validatePassenger(passenger));

      return {
         isValid: invalidPassengers.length === 0,
         invalidPassengers,
      };
   };

   return {
      calculateTotalPax,
      sanitizeName,
      sanitizeSurname,
      initializePassengers,
      validatePassenger,
      validateAllPassengers,
   };
}
